import React, {useState, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import {
  setSelectedOptionId,
  setSchdulerRequirementStatus,
  setRecurlyAccountInfo,
  selectRecurlyAccountInfo,
  selectSelectedOptionId,
  updateOption,
  updateProposalInfo,
  setProposalInfo,
} from '../../../../app-store/ProposalSlice'
import {
  useEnsureProposalInfo,
  isScheduleCallRequired,
} from '../../../../utils/client-proposal.util'
import {
  PTLogoWhite,
  PTKeapWhite,
  PTWarningIcon,
  PTChatIcon,
  PTSentSVG,
} from '../../../../assets'
import {ProposalPreview} from '../../components/ProposalPreview'
import {Button} from '../../../../core/shared/Button'
import {
  PageWrap,
  NavBar,
  NavLeft,
  NavRight,
  NavForLabel,
  NavBusinessName,
  GradientSection,
  HeroSection,
  HeroTop,
  HeroContent,
  HeroMeta,
  HeroTitle,
  HeroActions,
  NotQuiteRight,
  RequestChangesBtn,
  ValidityBanner,
  ValidityText,
  PricingDisclaimer,
  RepNotePanel,
  RepNoteTitle,
  RepNoteText,
  CardsSection,
  CardsInner,
  PageFooter,
  FooterText,
  Container,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalBody,
  ModalLabel,
  ModalTextarea,
  ModalFooter,
  ModalCloseBtn,
} from './styled.components'
import {useProposalAuthClient} from '../ClientProposalFlow'
import {useClientPaymentPreview} from '../ClientProposalFlow/hooks/use-client-payment-preview'
import {validateEmailWithApi} from '../../../../accountManagement/features/EmailLookup/EmailLookup.utils'
import {toast} from 'react-toastify'

function formatExpirationDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function PageShell({children, navRight}) {
  return (
    <PageWrap>
      <Container>
        <NavBar>
          <NavLeft>
            <img
              src={PTLogoWhite}
              alt="Thryv"
              style={{height: 30, objectFit: 'contain'}}
            />
            <span></span>
            <img
              src={PTKeapWhite}
              alt="Keap"
              style={{height: 30, objectFit: 'contain'}}
            />
          </NavLeft>
          {navRight}
        </NavBar>
        {children}
      </Container>
    </PageWrap>
  )
}

function ClientProposalView({proposalId: propProposalId}) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {proposalId: paramProposalId} = useParams()
  const proposalId = propProposalId || paramProposalId
  const client = useProposalAuthClient()
  const {proposalInfo, loading, error} = useEnsureProposalInfo({
    proposalId,
    client,
  })
  const {refreshClientPaymentPreview} = useClientPaymentPreview()
  const recurlyAccountInfo = useSelector(selectRecurlyAccountInfo)
  const currentSelectedOptionId = useSelector(selectSelectedOptionId)
  const [pricingLoading, setPricingLoading] = useState(false)

  useEffect(() => {
    if (!proposalInfo?.proposalId) return
    setPricingLoading(true)
    refreshClientPaymentPreview().finally(() => setPricingLoading(false))
  }, [proposalInfo?.proposalId]) // eslint-disable-line react-hooks/exhaustive-deps

  // For EXISTING client proposals, eagerly fetch Recurly account info as soon
  // as the proposal loads so the preview shows current subscriptions before
  // the client selects an option.
  useEffect(() => {
    if (!proposalInfo.clientEmail || proposalInfo.clientType !== 'EXISTING')
      return
    if (recurlyAccountInfo && Object.keys(recurlyAccountInfo).length > 0) return
    validateEmailWithApi({
      email: proposalInfo.clientEmail,
      client,
      flowType: 'Proposal',
    })
      .then(({meta}) => {
        if (meta?.subDetails) dispatch(setRecurlyAccountInfo(meta.subDetails))
      })
      .catch(() => {})
  }, [proposalInfo.clientEmail, proposalInfo.clientType]) // eslint-disable-line react-hooks/exhaustive-deps

  const [selectingOptionId, setSelectingOptionId] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [modalError, setModalError] = useState(null)
  const [requestSent, setRequestSent] = useState(false)

  // const checkIfScheduleCallRequired = useCallback(
  //   () => isScheduleCallRequired(proposalInfo),
  //   [proposalInfo],
  // )

  async function handleSelect(optionId) {
    setSelectingOptionId(optionId)
    dispatch(setSelectedOptionId(optionId))

    const {meta, outcome} = await validateEmailWithApi({
      email: proposalInfo.clientEmail,
      client,
      flowType: 'Proposal',
    })

    if (meta?.subDetails) {
      dispatch(setRecurlyAccountInfo(meta.subDetails))
    }

    // update option which is selected with isSelected: true and update the proposal via proposals/${proposalId} put call
    const selectedOption = (proposalInfo.options ?? []).find(
      opt => opt.optionId === optionId,
    )
    try {
      await client(`proposals/${proposalId}/updateStatus`, {
        data: {action: 'ACCEPTED', optionId: selectedOption.optionId},
        method: 'POST',
      })
      dispatch(setProposalInfo({...proposalInfo, statusCode: 'ACCEPTED'}))
      proposalInfo.options?.forEach(opt => {
        if (opt.isSelected) {
          dispatch(
            updateOption({
              displayOrder: opt.displayOrder,
              changes: {isSelected: false},
              skipDirty: true,
            }),
          )
        }
      })
      dispatch(
        updateOption({
          displayOrder: selectedOption.displayOrder,
          changes: {isSelected: true},
        }),
      )
    } catch (err) {
      toast.error(err.message || 'Failed to update selected option')
      return
    }

    // If the user switched to a different option and there's an existing
    // appointment, auto-cancel it so the appointment screen re-books based
    // on the new selection's products. Non-fatal — user can clear manually.
    if (proposalInfo.apptId && optionId !== currentSelectedOptionId) {
      try {
        await client(`plans/onboarding`, {
          data: {
            demoApptId: proposalInfo.apptId,
            type: 'onboarding',
            country: proposalInfo.country,
          },
          method: 'DELETE',
        })
        await client(`proposals/${proposalId}`, {
          data: {apptId: null},
          method: 'PUT',
        })
        dispatch(updateProposalInfo({apptId: null}))
      } catch {
        // Non-fatal: user can clear manually from the appointment screen
      }
    }

    // Use selectedOption directly — proposalInfo in the closure is stale and
    // won't reflect the isSelected dispatch until the next render cycle.
    const isScheduleRequired = isScheduleCallRequired({
      ...proposalInfo,
      options: (proposalInfo.options ?? []).map(opt => ({
        ...opt,
        isSelected: opt.optionId === optionId,
      })),
    })
    dispatch(setSchdulerRequirementStatus(isScheduleRequired))

    navigate(
      `/proposal-view/${proposalId}/${
        outcome !== 'new-customer'
          ? isScheduleRequired
            ? 'schedule-appointment'
            : 'client-payment'
          : 'account-information'
      }`,
    )
  }

  function closeModal() {
    setShowModal(false)
    setModalError(null)
  }

  async function handleSendRequest() {
    if (!feedback.trim() || submitting) return
    setSubmitting(true)
    setModalError(null)
    try {
      await client(`proposals/${proposalId}/updateStatus`, {
        data: {action: 'DECLINED', feedback: feedback.trim()},
      })
      dispatch(setProposalInfo({...proposalInfo, statusCode: 'DECLINED'}))

      closeModal()
      setFeedback('')
      setRequestSent(true)
    } catch (err) {
      setModalError(
        err.message || 'Failed to send your request. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (loading || error || !proposalInfo.proposalId) {
    const message = loading
      ? 'Loading proposal...'
      : error || 'Proposal not found.'
    return (
      <PageShell>
        <GradientSection
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <p style={{color: 'rgba(255,255,255,0.7)', fontSize: 16}}>
            {message}
          </p>
        </GradientSection>
      </PageShell>
    )
  }

  const displayName = `${proposalInfo.businessName ?? ''}`.trim()
  // const displayName =
  //   `${proposalInfo.clientFirstName ?? ''} ${
  //     proposalInfo.clientLastName ?? ''
  //   }`.trim() ||
  //   proposalInfo.clientEmail ||
  //   ''
  const expiresLabel = formatExpirationDate(proposalInfo.expirationDate)

  return (
    <PageShell
      navRight={
        <NavRight>
          <NavForLabel>Proposal for</NavForLabel>
          <NavBusinessName>{proposalInfo.businessName}</NavBusinessName>
        </NavRight>
      }
    >
      <GradientSection>
        <HeroSection>
          <HeroTop>
            <HeroContent>
              <HeroMeta>Your Personalized Proposal</HeroMeta>
              <HeroTitle>
                {displayName ? `Hi ${displayName}, here` : 'Here'} are your
                options for {proposalInfo.businessName}.
              </HeroTitle>
              <ValidityBanner>
                <img
                  src={PTWarningIcon}
                  alt="Warning Thryv"
                  width="16"
                  height="16"
                />
                <ValidityText>
                  This proposal is valid
                  {expiresLabel ? ` until ${expiresLabel}` : ' for 30 days'}.
                  After that, the link will expire and no longer be accessible.
                </ValidityText>
              </ValidityBanner>
              <PricingDisclaimer>
                *Pricing is subject to change. Final pricing will be presented
                during checkout.
              </PricingDisclaimer>
            </HeroContent>
            <HeroActions>
              {requestSent ? (
                <>
                  <NotQuiteRight
                    style={{display: 'flex', alignItems: 'center', gap: 6}}
                  >
                    <PTSentSVG width="14" height="14" />
                    Request sent!
                  </NotQuiteRight>
                  <span
                    style={{
                      fontSize: 12,
                      color: 'rgba(255,255,255,0.65)',
                      fontFamily: 'inherit',
                    }}
                  >
                    Your advisor will be in touch.
                  </span>
                </>
              ) : (
                <>
                  <NotQuiteRight>Not quite right?</NotQuiteRight>
                  <RequestChangesBtn
                    type="button"
                    onClick={() => setShowModal(true)}
                  >
                    <img
                      src={PTChatIcon}
                      alt="Chat Thryv"
                      width="16"
                      height="16"
                    />
                    Request Changes
                  </RequestChangesBtn>
                </>
              )}
            </HeroActions>
          </HeroTop>
        </HeroSection>

        <CardsSection>
          <CardsInner>
            {proposalInfo.repNotes && (
              <RepNotePanel>
                <RepNoteTitle>A note from your advisor</RepNoteTitle>
                <RepNoteText>{proposalInfo.repNotes}</RepNoteText>
              </RepNotePanel>
            )}
            <ProposalPreview
              onOptionSelect={handleSelect}
              client={true}
              selectingOptionId={selectingOptionId}
              pricingLoading={pricingLoading}
            />
          </CardsInner>
        </CardsSection>

        <PageFooter>
          <FooterText>Copyright &copy; 2026 Thryv, Inc.</FooterText>
        </PageFooter>
      </GradientSection>

      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalCloseBtn
              type="button"
              onClick={closeModal}
              aria-label="Close"
            >
              &times;
            </ModalCloseBtn>
            <ModalHeader>
              <ModalTitle>Request Changes</ModalTitle>
              <ModalSubtitle>
                Let your advisor know what changes you&apos;d like &mdash;
                they&apos;ll revise the proposal and send you an updated
                version.
              </ModalSubtitle>
            </ModalHeader>
            <ModalBody>
              <ModalLabel htmlFor="feedback-textarea">
                What would you like adjusted?
              </ModalLabel>
              <ModalTextarea
                id="feedback-textarea"
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                placeholder="e.g. I'd like to add the Website service, or remove Social Posts and lower the price..."
                disabled={submitting}
              />
              {modalError && (
                <p style={{color: '#ef4444', fontSize: 13, margin: '8px 0 0'}}>
                  {modalError}
                </p>
              )}
              <ModalFooter>
                <Button
                  type="button"
                  variant="solid-gradiant"
                  leftIcon={<PTSentSVG />}
                  onClick={handleSendRequest}
                  disabled={submitting || !feedback.trim()}
                >
                  {submitting ? 'Sending...' : 'Send Request to Advisor'}
                </Button>
              </ModalFooter>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </PageShell>
  )
}

export {ClientProposalView}
